<script setup lang="ts">
    import { useListboxNavigation } from '@/shared/composables';
    import { type CommandItemEntry, commandKey } from './context';
    import { type CommandFilter, defaultCommandFilter } from './filter';

    const props = withDefaults(
        defineProps<{
            /** Decides whether an item is found for the search. Default: every word of the search is in its value or keywords */
            filter?: CommandFilter;
            /** Hides the items the search does not find. `false` shows every item, for results filtered elsewhere (a server) */
            shouldFilter?: boolean;
            /** ArrowDown on the last item goes to the first, and ArrowUp back */
            loop?: boolean;
        }>(),
        { filter: undefined, shouldFilter: true, loop: true },
    );

    /** The search text */
    const search = defineModel<string>('search', { default: '' });
    /** The active item's value, as the keyboard or the pointer moves */
    const value = defineModel<string | undefined>('value', { default: undefined });

    const listId = useId();
    const list = shallowRef<HTMLElement | null>(null);

    /* Mounted items, in DOM order. */
    const entries = shallowRef<CommandItemEntry[]>([]);
    const sortByDom = (items: CommandItemEntry[]) =>
        [...items].sort((a, b) => {
            if (!a.element.value || !b.element.value) return 0;
            return a.element.value.compareDocumentPosition(b.element.value) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
        });

    function register(entry: CommandItemEntry) {
        entries.value = sortByDom([...entries.value, entry]);
        return () => {
            entries.value = entries.value.filter((other) => other !== entry);
        };
    }

    const entryOf = (id: string | undefined) => (id === undefined ? undefined : entries.value.find((entry) => entry.id === id));

    const searching = computed(() => search.value.trim() !== '');
    const isVisible = (entry: CommandItemEntry) =>
        !props.shouldFilter || !searching.value || (props.filter ?? defaultCommandFilter)(entry.value.value, search.value, entry.keywords.value);
    const isGroupVisible = (group: symbol) => entries.value.some((entry) => entry.group === group && isVisible(entry));

    const navItems = computed(() => entries.value.filter(isVisible).map((entry) => entry.id));

    /* Items register once they are in the DOM: until the Command is mounted, one is assumed, so CommandEmpty does not flash. */
    const mounted = ref(false);
    onMounted(() => (mounted.value = true));
    const hasVisibleItems = computed(() => !mounted.value || navItems.value.length > 0);

    const nav = useListboxNavigation({
        items: navItems,
        isDisabled: (id) => entryOf(id)?.disabled.value ?? false,
        loop: () => props.loop,
        homeEnd: false,
        /* A click, so the item's own click listeners run too */
        onSelect: (id) => entryOf(id)?.element.value?.click(),
    });

    const scrollToActive = () => entryOf(nav.active.value)?.element.value?.scrollIntoView({ block: 'nearest' });

    /* A new search starts from the first item it finds, at the top of the list. */
    watch(search, () => {
        nav.first();
        if (list.value) list.value.scrollTop = 0;
    });

    /* Items that arrive (the first render, results from a server) make the first one active when none is. */
    watch(navItems, () => {
        if (nav.active.value === undefined) nav.first();
    });

    watch(nav.active, (id) => {
        const next = entryOf(id)?.value.value;
        if (next !== value.value) value.value = next;
    });

    /* A value set from outside moves the active item. */
    watch(value, (next) => {
        if (next === undefined || entryOf(nav.active.value)?.value.value === next) return;
        const entry = entries.value.find((candidate) => candidate.value.value === next && isVisible(candidate) && !candidate.disabled.value);
        if (!entry) return;
        nav.highlight(entry.id);
        scrollToActive();
    });

    function onKeydown(event: KeyboardEvent) {
        if (event.defaultPrevented || event.isComposing) return;
        nav.onKeydown(event);
        if (event.defaultPrevented && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) scrollToActive();
    }

    provide(commandKey, {
        search: computed(() => search.value),
        setSearch: (text) => (search.value = text),
        searching,
        listId,
        list,
        activeId: computed(() => nav.active.value),
        hasVisibleItems,
        isVisible,
        isGroupVisible,
        highlight: nav.highlight,
        onKeydown,
        register,
    });
</script>

<template>
    <div
        data-slot="command"
        class="ui-command"
    >
        <slot />
    </div>
</template>

<style scoped>
    @layer components {
        .ui-command {
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            min-width: 0;
            overflow: hidden;
            border: 1px solid var(--color-border);
            border-radius: var(--radius-lg);
            background-color: var(--color-background-primary);
            color: var(--color-content-primary);
        }
    }
</style>
